/* eslint-disable camelcase */
import React, { useContext, useState } from "react"
import Header from "../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material"
import BackIcon from "../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import CustomConditionalActionsTableServer from "../../components/CustomConditionalActionsTableServerSide"
import normalFormatDate from "../utils/date/DDMMYYYFormatDate"
import { GlobalContext } from "../../contexts/GlobalContext"
import { api } from "../../api"
import CustomDatePicker from "../../components/CustomDatePicker"
import dayjs from "dayjs"
import Swal from "sweetalert2"

// Importación para generar íconos dinámicos según estándar
import getIconComponent from "../utils/getIconComponent"

import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import SearchIcon from "@mui/icons-material/Search"
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#2E7D32" },
  },
})

// Opciones de signo para búsqueda por monto
const listaSignos = [
  { id: "=", label: "Igual a (=)" },
  { id: ">", label: "Mayor que (>)" },
  { id: "<", label: "Menor que (<)" },
  { id: ">=", label: "Mayor o igual (>=)" },
  { id: "<=", label: "Menor o igual (<=)" },
]

const CobrosVistaIndex = () => {
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Controladores de estado visual: Inicia abierto para la primera búsqueda
  const [expanded, setExpanded] = useState(true)
  const [refreshTableKey, setRefreshTableKey] = useState(0)

  // Estado centralizado de filtros principales
  const [filtros, setFiltros] = useState({
    cxcnumcomp_ini: "",
    cxcnumcomp_fin: "",
    fec_ini: null,
    fec_fin: null,
    clinombre: "",
    facnumfac: "",
    cxcmonto: "",
    signo_monto: "=",
  })

  const handleInputChange = (field, value) => {
    setFiltros((prev) => ({ ...prev, [field]: value }))
  }

  const handleFiltrar = () => {
    // Al presionar buscar, la llave cambia, forzando a la tabla a recibir el endpoint real
    setRefreshTableKey((prev) => prev + 1)
    setExpanded(false)
  }

  // Ejecución de la Acción de Eliminar con Modal de Confirmación y Motivo
  const handleEliminar = async (row) => {
    const { cxcnumcomp } = row.original

    const result = await Swal.fire({
      title: `¿Eliminar Cobro ${cxcnumcomp}?`,
      text: "Esta acción reversará saldos y anulará transacciones. Ingrese el motivo:",
      input: "textarea",
      inputPlaceholder: "Escriba el motivo de la anulación aquí...",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, Eliminar",
      cancelButtonText: "Cancelar",
      preConfirm: (motivo) => {
        if (!motivo || motivo.trim() === "") {
          Swal.showValidationMessage("El motivo de eliminación es obligatorio")
        }
        return motivo
      },
    })

    if (result.isConfirmed) {
      try {
        Swal.fire({
          title: "Procesando...",
          text: "Revirtiendo saldos y transacciones, por favor espere.",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading()
          },
        })

        const response = await api.post("/CobrosVista/eliminarCobroVista", {
          cxcnumcomp,
          motivo: result.value,
        })

        Swal.fire("Eliminado", response.data.data || "Cobro eliminado con éxito.", "success")

        // Refresca la tabla automáticamente
        setRefreshTableKey((prev) => prev + 1)
      } catch (error) {
        console.error("Error al eliminar el cobro:", error)
        Swal.fire("Error", error.response?.data?.message || "Ocurrió un error al intentar eliminar el cobro.", "error")
      }
    }
  }

  // Helper monetario
  const formatMoneda = (valor) => {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(valor || 0)
  }

  // Helper para renderizar los colores de los estados
  const renderEstado = (status) => {
    const statusMap = {
      C: { text: "PROCESADO", color: "#2e7d32" },
      A: { text: "ANULADO", color: "#d32f2f" },
      P: { text: "PENDIENTE", color: "#ed6c02" },
    }
    const current = statusMap[status] || { text: status, color: "black" }

    return <span style={{ color: current.color, fontWeight: "bold" }}>{current.text}</span>
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
            color: "#196C87",
          }}
        >
          <MonetizationOnIcon sx={{ fontSize: 36, mr: 1 }} />
          <b>Bandeja de Cobros a la Vista</b>
        </div>

        <Box className={StyledRoot}>
          {/* ACORDEÓN DE FILTROS */}
          <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            sx={{ mb: 3, borderRadius: 2, boxShadow: 3, "&:before": { display: "none" } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "#f5f7fa", borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                Parámetros de Búsqueda
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {/* Fila 1: Comprobantes y Cliente */}
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    fullWidth
                    label="Comprobante Desde"
                    size="small"
                    value={filtros.cxcnumcomp_ini}
                    onChange={(e) => handleInputChange("cxcnumcomp_ini", e.target.value.toUpperCase())}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    fullWidth
                    label="Comprobante Hasta"
                    size="small"
                    value={filtros.cxcnumcomp_fin}
                    onChange={(e) => handleInputChange("cxcnumcomp_fin", e.target.value.toUpperCase())}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={6}>
                  <TextField
                    fullWidth
                    label="Código / RUC / Nombre del Cliente"
                    size="small"
                    value={filtros.clinombre}
                    onChange={(e) => handleInputChange("clinombre", e.target.value.toUpperCase())}
                  />
                </Grid>

                {/* Fila 2: Fechas y Factura */}
                <Grid item xs={12} sm={4} md={3}>
                  <CustomDatePicker
                    label="Fecha Emisión Desde"
                    value={filtros.fec_ini}
                    setValue={(v) => handleInputChange("fec_ini", v)}
                    isOptional={true}
                    format="DD/MM/YYYY"
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <CustomDatePicker
                    label="Fecha Emisión Hasta"
                    value={filtros.fec_fin}
                    setValue={(v) => handleInputChange("fec_fin", v)}
                    isOptional={true}
                    format="DD/MM/YYYY"
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={6}>
                  <TextField
                    fullWidth
                    label="Factura Interna Cobrada (Contiene)"
                    size="small"
                    value={filtros.facnumfac}
                    onChange={(e) => handleInputChange("facnumfac", e.target.value.toUpperCase())}
                  />
                </Grid>

                {/* Fila 3: Monto */}
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Condición de Monto"
                    size="small"
                    value={filtros.signo_monto}
                    onChange={(e) => handleInputChange("signo_monto", e.target.value)}
                  >
                    {listaSignos.map((op) => (
                      <MenuItem key={op.id} value={op.id}>
                        {op.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    fullWidth
                    label="Monto del Cobro"
                    type="number"
                    size="small"
                    value={filtros.cxcmonto}
                    onChange={(e) => handleInputChange("cxcmonto", e.target.value)}
                  />
                </Grid>

                {/* Botón de Acción */}
                <Grid item xs={12} display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<SearchIcon />}
                    onClick={handleFiltrar}
                    sx={{ px: 4, fontWeight: "bold" }}
                  >
                    Ejecutar Búsqueda
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* TABLA DE RESULTADOS */}
          <CustomConditionalActionsTableServer
            key={refreshTableKey}
            endpoint={refreshTableKey === 0 ? "" : "/CobrosVista/getAllCobro"}
            endpointJson={{
              externalFilters: {
                ...filtros,
                fec_ini: filtros.fec_ini ? dayjs(filtros.fec_ini).format("YYYY-MM-DD") : "",
                fec_fin: filtros.fec_fin ? dayjs(filtros.fec_fin).format("YYYY-MM-DD") : "",
              },
            }}
            errorMsgFilterSearch="Error al cargar los comprobantes de cobro."
            queryKeyModal="CobrosVistaData"
            perPage={15}
            rowActionsWidthTable={150}
            rowActions={(row) => {
              const acciones = []

              const buscarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action?.acccaption === "BUSCAR",
              )
              if (buscarAction) {
                acciones.push({
                  label: "Consultar",
                  key: "BUSCAR",
                  icon: getIconComponent(buscarAction?.accnameicono, buscarAction?.acctipoico),
                  onClick: (row) =>
                    navigate("buscar", {
                      state: { cxcnumcomp: row.original.cxcnumcomp },
                    }),
                })
              }

              const eliminarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action?.acccaption === "ELIMINAR",
              )
              // Solo habilitamos eliminar si el cobro NO está anulado ya
              if (eliminarAction && row.original.cxcstatus !== "A") {
                acciones.push({
                  label: "Eliminar / Anular",
                  key: "ELIMINAR",
                  icon: getIconComponent(eliminarAction?.accnameicono, eliminarAction?.acctipoico),
                  onClick: (row) => handleEliminar(row),
                })
              }

              return acciones
            }}
            topToolbarCustomActions={({ table, device }) => {
              const crearAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action.acccaption === "CREAR")

              const toolbarActions = []
              if (crearAction) {
                toolbarActions.push({
                  label: "Crear Nuevo Cobro",
                  key: crearAction?.acccaption,
                  icon: getIconComponent(crearAction?.accnameicono, crearAction?.acctipoico),
                  onClick: () => navigate("crear"),
                })
              }
              return toolbarActions
            }}
            columnsTable={[
              {
                accessorKey: "cxcnumcomp",
                header: "Comprobante",
                size: 150,
                Cell: ({ cell }) => <span style={{ fontWeight: "bold", color: "#196C87" }}>{cell.getValue()}</span>,
              },
              {
                accessorKey: "cxcfecemi",
                header: "Fecha Emisión",
                size: 120,
                Cell: ({ cell }) => <span>{normalFormatDate(cell.getValue())}</span>,
              },
              {
                accessorKey: "clinombre",
                header: "Cliente",
                size: 250,
              },
              {
                accessorKey: "cxcmonto",
                header: "Monto",
                size: 120,
                Cell: ({ cell }) => <span style={{ fontWeight: "bold" }}>{formatMoneda(cell.getValue())}</span>,
              },
              {
                accessorKey: "cxcstatus",
                header: "Estado",
                size: 130,
                Cell: ({ cell }) => renderEstado(cell.getValue()),
              },
              {
                accessorKey: "cxcorigen_desc",
                header: "Origen",
                size: 220,
              },
              {
                accessorKey: "cjadescri",
                header: "Caja",
                size: 150,
              },
              {
                accessorKey: "cxcusrisys",
                header: "Usuario",
                size: 110,
              },
            ]}
          />
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default CobrosVistaIndex
